import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { GlassCard } from '../components/ui/GlassCard';
import { NeonButton } from '../components/ui/NeonButton';
import { LoaderIcon, UserIcon, UsersIcon } from '../components/Icons';

interface RegisterFormProps {
  onBack: () => void;
}

export default function RegisterForm({ onBack }: RegisterFormProps) {
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: '' as 'client' | 'pt' | '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.email || !formData.name || !formData.role) {
      setError('Please fill in all fields');
      return;
    }

    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (formData.name.length < 2) {
      setError('Name must be at least 2 characters long');
      return;
    }

    setLoading(true);

    try {
      // Backend requires: email, name, role (password is optional for demo)
      await register(formData.email, '', formData.name, formData.role);
    } catch (err: any) {
      console.error('Registration error:', err);
      const errorMessage = err.message || 'Registration failed';

      // Provide user-friendly messages for specific errors
      if (errorMessage.includes('exists') ||
          errorMessage.includes('duplicate') ||
          errorMessage.includes('UNIQUE constraint failed') ||
          errorMessage.includes('email already')) {
        setError('An account with this email already exists. Please sign in instead.');
      } else if (errorMessage.includes('Invalid email')) {
        setError('Please enter a valid email address.');
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        // Show the actual error message from the server
        setError(errorMessage);
      }
      setLoading(false);
    }
  };

  const handleRoleSelect = (role: 'client' | 'pt') => {
    setFormData({ ...formData, role });
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <GlassCard className="p-10 w-full max-w-xl">
        <button
          onClick={onBack}
          className="text-gray-400 hover:text-white mb-4 transition-colors"
        >
          ← Back
        </button>

        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">Create Your Account</h2>
          <p className="text-cyan-400/80">Join AetherFit and start your fitness journey</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
              Full Name
            </label>
            <input
              type="text"
              id="name"
              data-testid="register-name-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 bg-gray-800/50 border border-cyan-300/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all"
              placeholder="John Doe"
              disabled={loading}
              autoFocus
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              data-testid="register-email-input"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-3 bg-gray-800/50 border border-cyan-300/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all"
              placeholder="your.email@example.com"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">
              I am a...
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                data-testid="register-role-client"
                onClick={() => handleRoleSelect('client')}
                disabled={loading}
                className={`
                  p-6 rounded-xl border-2 transition-all duration-300
                  ${formData.role === 'client'
                    ? 'border-cyan-400 bg-cyan-400/10 text-white'
                    : 'border-cyan-300/30 bg-gray-800/30 text-gray-400 hover:border-cyan-400/50 hover:text-gray-200'
                  }
                  ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  flex flex-col items-center gap-3
                `}
              >
                <UserIcon className="h-10 w-10" />
                <span className="font-bold text-lg">Client</span>
                <span className="text-xs text-center">
                  Looking for training and guidance
                </span>
              </button>

              <button
                type="button"
                data-testid="register-role-pt"
                onClick={() => handleRoleSelect('pt')}
                disabled={loading}
                className={`
                  p-6 rounded-xl border-2 transition-all duration-300
                  ${formData.role === 'pt'
                    ? 'border-cyan-400 bg-cyan-400/10 text-white'
                    : 'border-cyan-300/30 bg-gray-800/30 text-gray-400 hover:border-cyan-400/50 hover:text-gray-200'
                  }
                  ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  flex flex-col items-center gap-3
                `}
              >
                <UsersIcon className="h-10 w-10" />
                <span className="font-bold text-lg">Personal Trainer</span>
                <span className="text-xs text-center">
                  Ready to coach and guide clients
                </span>
              </button>
            </div>
          </div>

          <div className="pt-2">
            <NeonButton
              type="submit"
              data-testid="register-submit-button"
              disabled={loading || !formData.role}
              className="w-full py-3 text-lg flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <LoaderIcon className="h-5 w-5" />
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </NeonButton>
          </div>
        </form>

        <div className="mt-6 pt-6 border-t border-cyan-300/20 text-center">
          <p className="text-sm text-gray-400">
            By creating an account, you agree to our terms and privacy policy
          </p>
        </div>
      </GlassCard>
    </div>
  );
}
